import React, { useState, useEffect, useRef } from "react";
import { IoMdClose, IoMdAlert } from "react-icons/io";
import { FaCheckCircle, FaUndo } from "react-icons/fa";
import CameraComponent from "./CameraComponent";
import LoadingOverlay from "./LoadingOverlay";
import Api from "../Api";

const ACTIONS = {
  masuk: { method: "post", url: "/absensi/check-in", label: "Absen Masuk" },
  mulai_istirahat: {
    method: "post",
    url: "/absensi/istirahat-mulai",
    label: "Mulai Istirahat",
  },
  selesai_istirahat: {
    method: "put",
    url: "/absensi/istirahat-selesai",
    label: "Selesai Istirahat",
  },
  pulang: { method: "put", url: "/absensi/check-out", label: "Absen Pulang" },
};

// error_code yang membuat tampilan perlu disinkronkan ulang (409)
const PERLU_REFRESH = new Set([
  "ALREADY_CHECKED_IN",
  "NOT_CHECKED_IN",
  "ALREADY_CHECKED_OUT",
  "BREAK_ALREADY_STARTED",
  "BREAK_STILL_ACTIVE",
  "NO_ACTIVE_BREAK",
  "BREAK_ALREADY_ENDED",
]);

const CameraAttendance = ({
  isOpen,
  onClose,
  mode,
  shiftId,
  onAttendanceSuccess,
  onStatusChange,
}) => {
  const [image, setImage] = useState(null);
  const [showCamera, setShowCamera] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorDialog, setErrorDialog] = useState(null);
  const cameraRef = useRef(); // Ref untuk memicu capture

  useEffect(() => {
    if (isOpen) {
      setImage(null);
      setShowCamera(true);
      setErrorDialog(null);
    }
  }, [isOpen]);

  const handleCapture = (imageSrc) => {
    setImage(imageSrc);
    setShowCamera(false);
  };

  const triggerCapture = () => {
    if (cameraRef.current) {
      cameraRef.current.capture();
    }
  };

  const resizeImage = (base64Str) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        let canvas = document.createElement("canvas");
        const MAX_SIZE = 640;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
    });
  };

  const resetFoto = () => {
    setErrorDialog(null);
    setImage(null);
    setShowCamera(true);
  };

  // Tangani error berdasarkan error_code (lihat ERROR_ABSENSI_STATUS.md)
  const handleError = (res) => {
    // 401 sudah ditangani interceptor di Api.js (refresh token / logout)
    if (res.status === 401) return;

    // 409: status absensi tidak sinkron -> tutup modal & refresh status hari ini
    if (PERLU_REFRESH.has(res.errorCode) || res.status === 409) {
      onStatusChange?.(
        res.errorCode === "BREAK_ALREADY_ENDED" ? null : res.message,
      );
      onClose();
      return;
    }

    switch (res.errorCode) {
      case "FACE_MISMATCH":
      case "VALIDATION_ERROR":
        return setErrorDialog({
          title: "Verifikasi Wajah Gagal",
          message: res.message,
          actionLabel: "Ambil Foto Ulang",
          onAction: resetFoto,
        });

      case "OUTSIDE_RADIUS": {
        const e = res.errors || {};
        return setErrorDialog({
          title: "Di Luar Radius",
          message: res.message,
          detail: e.lokasi_terdekat
            ? `Lokasi terdekat: ${e.lokasi_terdekat} — ${e.jarak_meter} m (maks ${e.radius_meter} m)`
            : null,
          actionLabel: "Perbarui Lokasi",
          onAction: () => {
            setErrorDialog(null);
            handleSave();
          },
        });
      }

      case "BREAK_START_OUT_OF_WINDOW":
      case "BREAK_END_OUT_OF_WINDOW":
        return setErrorDialog({
          title: "Di Luar Jam Istirahat",
          message: res.message,
          detail: res.errors?.jam_awal
            ? `Jam istirahat: ${res.errors.jam_awal} – ${res.errors.jam_akhir} WITA`
            : null,
        });

      case "LOCATION_NOT_REGISTERED":
      case "SHIFT_NOT_ALLOWED":
      case "INVALID_ATTENDANCE_DATA":
        return setErrorDialog({
          title: "Tidak Dapat Absen",
          message: res.message,
          detail: "Silakan hubungi admin/HR.",
        });

      case "SHIFT_NOT_FOUND":
        return setErrorDialog({
          title: "Shift Tidak Ditemukan",
          message: res.message,
          detail: "Silakan pilih ulang shift Anda.",
        });

      case "INVALID_INPUT":
        console.error("Field kurang:", res.errors?.fields);
        return setErrorDialog({
          title: "Data Tidak Lengkap",
          message: res.message,
          detail: "Pastikan izin kamera & lokasi aktif.",
          actionLabel: "Ambil Foto Ulang",
          onAction: resetFoto,
        });

      case "NETWORK_ERROR":
        return setErrorDialog({
          title: "Koneksi Bermasalah",
          message: "Periksa koneksi internet Anda, lalu coba lagi.",
          actionLabel: "Coba Lagi",
          onAction: () => {
            setErrorDialog(null);
            handleSave();
          },
        });

      default:
        if (res.status >= 500) {
          return setErrorDialog({
            title: "Terjadi Kesalahan",
            message: "Terjadi kesalahan, coba beberapa saat lagi.",
          });
        }
        if (res.status === 400) console.error("Absensi 400:", res);
        return setErrorDialog({
          title: "Gagal",
          message: res.message || "Terjadi kesalahan, coba beberapa saat lagi.",
          ...(res.status === 422 && {
            actionLabel: "Coba Lagi",
            onAction: resetFoto,
          }),
        });
    }
  };

  const kirimAbsensi = async (formData) => {
    const action = ACTIONS[mode];
    if (!action) {
      return {
        ok: false,
        status: 0,
        errorCode: "UNKNOWN",
        message: "Mode absensi tidak dikenal",
      };
    }
    try {
      const res = await Api({
        method: action.method,
        url: action.url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return { ok: true, message: res.data?.message, data: res.data?.data };
    } catch (err) {
      const body = err.response?.data;
      return {
        ok: false,
        status: err.response?.status ?? 0,
        errorCode:
          body?.error_code ?? (err.response ? "UNKNOWN" : "NETWORK_ERROR"),
        message: body?.message ?? "Tidak dapat terhubung ke server",
        errors: body?.errors ?? null,
      };
    }
  };

  const handleSave = () => {
    if (isLoading || !image) return; // cegah double tap
    if (!navigator.geolocation) {
      setErrorDialog({
        title: "Lokasi Tidak Didukung",
        message: "Perangkat Anda tidak mendukung GPS.",
      });
      return;
    }
    setIsLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const resizedImage = await resizeImage(image);
          const blob = await (await fetch(resizedImage)).blob();

          const formData = new FormData();
          formData.append("file", blob, "absensi.jpg");
          formData.append("latitude", latitude);
          formData.append("longitude", longitude);
          if (mode === "masuk" && shiftId) {
            formData.append("id_jam_kerja", shiftId);
          }

          const res = await kirimAbsensi(formData);
          if (res.ok) {
            onAttendanceSuccess?.(res.message);
            onClose();
          } else {
            handleError(res);
          }
        } catch (error) {
          console.error(error);
          setErrorDialog({
            title: "Terjadi Kesalahan",
            message: "Gagal memproses foto. Silakan coba lagi.",
            actionLabel: "Ambil Foto Ulang",
            onAction: resetFoto,
          });
        } finally {
          setIsLoading(false);
        }
      },
      () => {
        setIsLoading(false);
        setErrorDialog({
          title: "Izin Lokasi Diperlukan",
          message: "Aktifkan izin lokasi (GPS) untuk melakukan absensi.",
          actionLabel: "Coba Lagi",
          onAction: () => {
            setErrorDialog(null);
            handleSave();
          },
        });
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in duration-300">
      {isLoading && <LoadingOverlay message="Memproses Absensi..." />}

      <div className="bg-white w-full max-w-sm rounded-[45px] overflow-hidden shadow-2xl relative flex flex-col items-center p-6 pt-12 animate-in slide-in-from-bottom-10">
        {/* Tombol Close */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-gray-400 hover:text-custom-merah transition-colors"
        >
          <IoMdClose size={28} />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-xl font-black text-custom-gelap uppercase tracking-widest">
            Konfirmasi Kehadiran
          </h2>
          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1">
            {ACTIONS[mode]?.label || mode?.replace("_", " ")}
          </p>
        </div>

        {/* Camera Area */}
        <div className="relative w-full aspect-[3/4] bg-gray-900 rounded-[35px] overflow-hidden shadow-2xl border-4 border-white">
          {showCamera ? (
            <CameraComponent ref={cameraRef} onCapture={handleCapture} />
          ) : (
            <img
              src={image}
              alt="Preview"
              className="w-full h-full object-cover scale-x-[-1]"
            />
          )}

          {/* Frame Face Overlay (Hanya saat kamera aktif) */}
          {showCamera && (
            <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
              <div className="w-64 h-80 border-2 border-dashed border-white/50 rounded-[100px]"></div>
              <p className="text-white/70 text-[10px] mt-4 font-medium bg-black/20 px-4 py-1 rounded-full backdrop-blur-sm">
                Posisikan wajah di tengah oval
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 w-full flex flex-col items-center">
          {showCamera ? (
            /* Tombol Shutter (Ambil Foto) */
            <button
              onClick={triggerCapture}
              className="w-16 h-16 rounded-full border-4 border-gray-200 p-1 active:scale-90 transition-transform"
            >
              <div className="w-full h-full bg-custom-merah rounded-full shadow-inner"></div>
            </button>
          ) : (
            /* Tombol Konfirmasi */
            <div className="flex gap-4 w-full px-4">
              <button
                onClick={() => setShowCamera(true)}
                className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 transition"
              >
                <FaUndo /> Ulangi
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex-1 py-4 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-green-200 flex items-center justify-center gap-2 active:scale-95 transition"
              >
                <FaCheckCircle /> Kirim
              </button>
            </div>
          )}
        </div>

        {/* Dialog Error */}
        {errorDialog && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6">
            <div className="bg-white w-full rounded-[35px] p-6 shadow-2xl text-center animate-in zoom-in duration-200">
              <div className="w-14 h-14 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
                <IoMdAlert size={30} className="text-custom-merah" />
              </div>
              <h3 className="text-sm font-black text-custom-gelap uppercase tracking-widest">
                {errorDialog.title}
              </h3>
              <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                {errorDialog.message}
              </p>
              {errorDialog.detail && (
                <p className="text-[10px] text-gray-400 font-bold mt-2 leading-relaxed">
                  {errorDialog.detail}
                </p>
              )}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setErrorDialog(null)}
                  className="flex-1 py-3 bg-gray-100 text-gray-500 rounded-2xl font-black text-[10px] uppercase tracking-widest active:scale-95 transition"
                >
                  Tutup
                </button>
                {errorDialog.onAction && (
                  <button
                    onClick={errorDialog.onAction}
                    className="flex-1 py-3 bg-gradient-to-r from-custom-merah to-custom-gelap text-white rounded-2xl font-black text-[10px] uppercase tracking-widest active:scale-95 transition"
                  >
                    {errorDialog.actionLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraAttendance;
